import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    Button,
    Typography,
    Box,
    Card,
    Divider,
    Alert,
    AlertTitle,
    Chip,
    Stack,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';

const StudentList = (props) => {
    const [rowSelection, setRowSelection] = useState({});
    const [teacherRowSelection, setTeacherRowSelection] = useState({});
    const [tabTeachers, setTabTeachers] = useState([]);
    const [tabStudents, setTabStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [selectedTeachers, setSelectedTeachers] = useState([]);
    const [emails, setEmails] = useState('');
    const [teacherEmails, setTeacherEmails] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [importError, setImportError] = useState('');
    const [importSuccess, setImportSuccess] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDeleteTeacherConfirm, setShowDeleteTeacherConfirm] = useState(false);
    const [revealedPasswords, setRevealedPasswords] = useState({});
    const [showAllPasswords, setShowAllPasswords] = useState(false);
    const [copiedEmail, setCopiedEmail] = useState(null);

    const {
        isAdmin,
        apiAuthResponse,
        students,
        setStudents,
        teachers,
        setTeachers,
        labType,
        labVersion,
    } = props;

    const labelStudentBooked = isAdmin ? "Studenti iscritti" : "Propri studenti iscritti";

    // La colonna password è visibile solo per gli admin sui laboratori d'esame v2
    const showPasswordColumn = isAdmin && labType === 'exam' && labVersion === 2;

    const toggleRevealPassword = (email) => {
        setRevealedPasswords(prev => ({ ...prev, [email]: !prev[email] }));
    };

    const handleCopyPassword = async (email, password) => {
        try {
            await navigator.clipboard.writeText(password);
            setCopiedEmail(email);
            setTimeout(() => setCopiedEmail(null), 1500);
        } catch (err) {
            console.log('Impossibile copiare la password', err);
        }
    };

    const teacherColumns = useMemo(
        () => [
            {
                accessorKey: 'teacher',
                header: 'Email Docente',
                size: 250,
            },
            {
                accessorKey: 'registrationStatus',
                header: 'Stato',
                size: 120,
                Cell: ({ cell }) => (
                    <Chip
                        label={getStatusLabel(cell.getValue())}
                        color={getStatusColor(cell.getValue())}
                        size="small"
                        variant="outlined"
                    />
                ),
            },
        ],
        [],
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'registered':
                return 'success';
            case 'queued':
                return 'info';
            case 'error':
                return 'error';
            case 'dropped':
                return 'default';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'registered':
                return 'Registrato';
            case 'queued':
                return 'In coda';
            case 'error':
                return 'Errore';
            case 'dropped':
                return 'Rimosso';
            default:
                return status || 'N/D';
        }
    };

    const studentsColumns = useMemo(
        () => [
            {
                accessorKey: 'email',
                header: 'Email Studente',
                size: 180,
            },
            {
                accessorKey: 'teacher.email',
                header: 'Docente',
                size: 150,
            },
            {
                accessorKey: 'partition',
                header: 'Turno',
                size: 80,
            },
            {
                accessorKey: 'registrationStatus',
                header: 'Stato',
                size: 120,
                Cell: ({ cell }) => (
                    <Chip
                        label={getStatusLabel(cell.getValue())}
                        color={getStatusColor(cell.getValue())}
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                accessorKey: "registrationDate",
                header: "Data iscrizione",
                size: 140,
                Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString('it-IT'),
            },
            ...(showPasswordColumn ? [{
                accessorKey: 'password',
                header: 'Password',
                size: 220,
                enableSorting: false,
                enableColumnFilter: false,
                enableGlobalFilter: false,
                Header: () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>Password</span>
                        <Tooltip title={showAllPasswords ? 'Nascondi tutte le password' : 'Mostra tutte le password'}>
                            <IconButton
                                size="small"
                                onClick={() => setShowAllPasswords(prev => !prev)}
                            >
                                {showAllPasswords
                                    ? <VisibilityOffIcon fontSize="small" />
                                    : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
                Cell: ({ row }) => {
                    const { email, password } = row.original;
                    if (!password) {
                        return <Typography variant="body2" color="textSecondary">—</Typography>;
                    }
                    const isRevealed = showAllPasswords || !!revealedPasswords[email];
                    const isCopied = copiedEmail === email;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: 'monospace',
                                    letterSpacing: isRevealed ? 0.3 : 2,
                                    minWidth: 120,
                                    userSelect: isRevealed ? 'text' : 'none',
                                }}
                            >
                                {isRevealed ? password : '•'.repeat(10)}
                            </Typography>
                            <Tooltip title={isRevealed ? 'Nascondi password' : 'Mostra password'}>
                                <IconButton
                                    size="small"
                                    onClick={() => toggleRevealPassword(email)}
                                    aria-label={isRevealed ? 'Nascondi password' : 'Mostra password'}
                                >
                                    {isRevealed
                                        ? <VisibilityOffIcon fontSize="small" />
                                        : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={isCopied ? 'Copiata!' : 'Copia password'}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleCopyPassword(email, password)}
                                    aria-label="Copia password"
                                >
                                    {isCopied
                                        ? <CheckIcon fontSize="small" color="success" />
                                        : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                },
            }] : []),
        ],
        [showPasswordColumn, revealedPasswords, showAllPasswords, copiedEmail],
    );

    const handleImportStudents = () => {
        setImportError('');
        setImportSuccess('');

        const emailArray = emails.split(',').map(email => email.trim()).filter(email => email);
        if (emailArray.length === 0) {
            setImportError('Inserisci almeno un indirizzo email');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const validEmails = emailArray.filter(email => emailRegex.test(email));
        const invalidEmails = emailArray.filter(email => !emailRegex.test(email));

        // Separa studenti già presenti (attivi) dai recuperabili (dropped o unregistered)
        const duplicateEmails = validEmails.filter(email =>
            tabStudents.some(student => student.email === email && student.registrationStatus !== 'dropped' && student.registrationStatus !== 'unregistered')
        );
        const recoverableEmails = validEmails.filter(email =>
            tabStudents.some(student => student.email === email && (student.registrationStatus === 'dropped' || student.registrationStatus === 'unregistered'))
        );

        // Crea nuovi studenti
        const newStudents = validEmails
            .filter(email => !tabStudents.some(student => student.email === email))
            .map(email => ({
                teacher: {
                    email: apiAuthResponse,
                    partition: 0,
                },
                email,
                partition: 0,
                registrationDate: new Date().toISOString(),
                userType: 'standard',
                registrationStatus: 'queued',
            }));

        // Recupera gli studenti dropped/unregistered (cambia status a 'queued')
        let updatedTabStudents = [...tabStudents];
        if (recoverableEmails.length > 0) {
            updatedTabStudents = updatedTabStudents.map(student =>
                recoverableEmails.includes(student.email)
                    ? { ...student, registrationStatus: 'queued' }
                    : student
            );
        }

        if (invalidEmails.length > 0) {
            setImportError(`Email non valide: ${invalidEmails.join(', ')}`);
        }
        if (duplicateEmails.length > 0) {
            setImportError(prev => prev ? `${prev}. Email già presenti: ${duplicateEmails.join(', ')}` : `Email già presenti: ${duplicateEmails.join(', ')}`);
        }

        // Aggiungi nuovi studenti e recupera i dropped/unregistered
        if (newStudents.length > 0 || recoverableEmails.length > 0) {
            updatedTabStudents = [...updatedTabStudents, ...newStudents];
            setTabStudents(updatedTabStudents);
            setStudents(updatedTabStudents);

            let successMsg = '';
            if (newStudents.length > 0) successMsg += `${newStudents.length} studente/i aggiunto/i`;
            if (recoverableEmails.length > 0) {
                if (successMsg) successMsg += ' e ';
                successMsg += `${recoverableEmails.length} studente/i recuperato/i`;
            }
            successMsg += ' con successo';

            setImportSuccess(successMsg);
            setEmails('');
            setTimeout(() => setImportSuccess(''), 3000);
        }
    };

    const handleImportTeachers = () => {
        setImportError('');
        setImportSuccess('');

        const emailArray = teacherEmails.split(',').map(email => email.trim()).filter(email => email);
        if (emailArray.length === 0) {
            setImportError('Inserisci almeno un indirizzo email');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const validEmails = emailArray.filter(email => emailRegex.test(email));
        const invalidEmails = emailArray.filter(email => !emailRegex.test(email));

        // Separa docenti già presenti (attivi) dai recuperabili (dropped o unregistered)
        const duplicateEmails = validEmails.filter(email =>
            tabTeachers.some(teacher => teacher.teacher === email && teacher.registrationStatus !== 'dropped' && teacher.registrationStatus !== 'unregistered')
        );
        const recoverableEmails = validEmails.filter(email =>
            tabTeachers.some(teacher => teacher.teacher === email && (teacher.registrationStatus === 'dropped' || teacher.registrationStatus === 'unregistered'))
        );

        // Crea nuovi docenti
        const newTeachers = validEmails
            .filter(email => !tabTeachers.some(teacher => teacher.teacher === email))
            .map(email => ({
                teacher: email,
                registrationStatus: 'queued',
                registrationDate: new Date().toISOString(),
            }));

        // Recupera i docenti dropped/unregistered (cambia status a 'queued')
        let updatedTabTeachers = [...tabTeachers];
        if (recoverableEmails.length > 0) {
            updatedTabTeachers = updatedTabTeachers.map(teacher =>
                recoverableEmails.includes(teacher.teacher)
                    ? { ...teacher, registrationStatus: 'queued' }
                    : teacher
            );
        }

        if (invalidEmails.length > 0) {
            setImportError(`Email non valide: ${invalidEmails.join(', ')}`);
        }
        if (duplicateEmails.length > 0) {
            setImportError(prev => prev ? `${prev}. Email già presenti: ${duplicateEmails.join(', ')}` : `Email già presenti: ${duplicateEmails.join(', ')}`);
        }

        // Aggiungi nuovi docenti e recupera i dropped/unregistered
        if (newTeachers.length > 0 || recoverableEmails.length > 0) {
            updatedTabTeachers = [...updatedTabTeachers, ...newTeachers];
            setTabTeachers(updatedTabTeachers);
            const formattedTeachers = updatedTabTeachers
                .filter(t => t.registrationStatus !== 'dropped' && t.registrationStatus !== 'unregistered')
                .map(teacher => ({
                    email: teacher.teacher,
                    partitions: [0],
                    registrationStatus: teacher.registrationStatus,
                    registrationDate: teacher.registrationDate,
                }));
            setTeachers(formattedTeachers);

            let successMsg = '';
            if (newTeachers.length > 0) successMsg += `${newTeachers.length} docente/i aggiunto/i`;
            if (recoverableEmails.length > 0) {
                if (successMsg) successMsg += ' e ';
                successMsg += `${recoverableEmails.length} docente/i recuperato/i`;
            }
            successMsg += ' con successo';

            setImportSuccess(successMsg);
            setTeacherEmails('');
            setTimeout(() => setImportSuccess(''), 3000);
        }
    };

    // Filtra i docenti per mostrare solo quelli non rimossi (non 'dropped')
    const activeTeachers = useMemo(() => 
        tabTeachers.filter(teacher =>
            teacher.registrationStatus !== 'dropped' && teacher.registrationStatus !== 'unregistered'
        ), [tabTeachers]
    );

    const teachersTable = useMaterialReactTable({
        columns: teacherColumns,
        data: activeTeachers || [],
        enableBottomToolbar: false,
        enableRowSelection: true,
        enableSelectAll: true,
        onRowSelectionChange: setTeacherRowSelection,
        state: { rowSelection: teacherRowSelection },
        muiTableContainerProps: { sx: { maxHeight: '300px' } },
    });

    // Filtra gli studenti per mostrare solo quelli non rimossi (non 'dropped')
    const activeStudents = useMemo(() => 
        tabStudents.filter(student =>
            student.registrationStatus !== 'dropped' && student.registrationStatus !== 'unregistered'
        ), [tabStudents]
    );

    const studentsTable = useMaterialReactTable({
        columns: studentsColumns,
        data: activeStudents || [],
        enableBottomToolbar: false,
        enableGlobalFilterModes: true,
        enablePagination: false,
        enableColumnPinning: true,
        enableRowVirtualization: true,
        enableRowSelection: true,
        enableSelectAll: true,
        onRowSelectionChange: setRowSelection,
        state: { rowSelection },
        muiTableContainerProps: { sx: { maxHeight: '400px' } },
    });

    // Inizializza le tabelle una sola volta quando i dati arrivano
    useEffect(() => {
        if ((students || teachers) && !isInitialized) {
            const tabStudent = [];
            const tabTeacher = [];

            if (students && students.length > 0) {
                students.forEach((value) => {
                    tabStudent.push({
                        ...value,
                        teacher: value.teacher,
                        partition: value.teacher?.partition,
                        email: value.email,
                        registrationDate: value.registrationDate,
                        registrationStatus: value.registrationStatus || 'registered',
                    });
                });
            }

            if (teachers && teachers.length > 0) {
                teachers.forEach((value) => {
                    tabTeacher.push({
                        teacher: value.email,
                        registrationStatus: value.registrationStatus || 'registered',
                        registrationDate: value.registrationDate || new Date().toISOString(),
                    });
                });
            }

            setTabStudents(tabStudent);
            setTabTeachers(tabTeacher);

            // Sincronizza anche lo state del parent con i dati iniziali
            // Questo è necessario per la clonazione dove il parent parte con array vuoti
            if (setStudents && tabStudent.length > 0) {
                setStudents(tabStudent);
            }
            if (setTeachers && tabTeacher.length > 0) {
                const formattedTeachers = tabTeacher.map(t => ({
                    email: t.teacher,
                    partitions: [0],
                    registrationStatus: t.registrationStatus,
                    registrationDate: t.registrationDate,
                }));
                setTeachers(formattedTeachers);
            }

            setIsInitialized(true);
        }
    }, [students, teachers, apiAuthResponse, isAdmin, isInitialized, setStudents, setTeachers]);

    // Gestisce la selezione degli studenti
    useEffect(() => {
        if (!activeStudents || activeStudents.length === 0) {
            setSelectedStudents([]);
            return;
        }

        const selectedEmails = Object.keys(rowSelection)
            .filter(index => rowSelection[index] && activeStudents[index])
            .map(index => activeStudents[index].email);

        setSelectedStudents(selectedEmails);
    }, [rowSelection, activeStudents]);

    const handleDeleteSelected = () => {
        // Aggiorna lo stato a 'dropped' senza rimuovere dalla lista
        const updatedTabStudents = tabStudents.map(student =>
            selectedStudents.includes(student.email)
                ? { ...student, registrationStatus: 'dropped' }
                : student
        );

        setTabStudents(updatedTabStudents);
        setStudents(updatedTabStudents);
        setRowSelection({});
        setShowDeleteConfirm(false);
    };

    const handleDeleteSelectedTeachers = () => {
        // Aggiorna lo stato a 'dropped' senza rimuovere dalla lista
        const updatedTabTeachers = tabTeachers.map(teacher =>
            selectedTeachers.includes(teacher.teacher)
                ? { ...teacher, registrationStatus: 'dropped' }
                : teacher
        );

        setTabTeachers(updatedTabTeachers);
        // Passa TUTTI i docenti (inclusi quelli dropped) al parent
        const formattedTeachers = updatedTabTeachers.map(t => ({
            email: t.teacher,
            partitions: [0],
            registrationStatus: t.registrationStatus,
            registrationDate: t.registrationDate,
        }));
        setTeachers(formattedTeachers);
        setTeacherRowSelection({});
        setShowDeleteTeacherConfirm(false);
    };

    // Gestisce la selezione dei docenti
    useEffect(() => {
        if (!activeTeachers || activeTeachers.length === 0) {
            setSelectedTeachers([]);
            return;
        }

        const selectedEmails = Object.keys(teacherRowSelection)
            .filter(index => teacherRowSelection[index] && activeTeachers[index])
            .map(index => activeTeachers[index].teacher);

        setSelectedTeachers(selectedEmails);
    }, [teacherRowSelection, activeTeachers]);



    return (
        <React.Fragment>
            {/* SEZIONE DOCENTI */}
            <Card sx={{ padding: 3, marginBottom: 3, boxShadow: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <PersonAddIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Gestione Docenti
                    </Typography>
                </Box>
                <Divider sx={{ marginBottom: 2 }} />

                {/* Tabella docenti */}
                {tabTeachers.length > 0 && (
                    <Box sx={{ marginBottom: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1.5 }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                    Docenti attuali ({activeTeachers.length})
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Seleziona i docenti che vuoi rimuovere
                                </Typography>
                            </Box>
                            {selectedTeachers.length > 0 && (
                                <Chip
                                    label={`${selectedTeachers.length} selezionato/i`}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Paper variant="outlined">
                            <MaterialReactTable table={teachersTable} />
                        </Paper>
                        {selectedTeachers.length > 0 && (
                            <Button
                                onClick={() => setShowDeleteTeacherConfirm(true)}
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                sx={{ marginTop: 1.5 }}
                            >
                                Rimuovi {selectedTeachers.length} docente/i
                            </Button>
                        )}
                    </Box>
                )}

                {/* Form importa docenti */}
                <Box sx={{ marginTop: 2 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 500 }}>
                        Aggiungi nuovi docenti
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: 1 }}>
                        Inserisci gli indirizzi email separati da virgole
                    </Typography>
                    <TextField
                        value={teacherEmails}
                        onChange={(e) => setTeacherEmails(e.target.value)}
                        placeholder="esempio1@unimib.it, esempio2@unimib.it"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        size="small"
                    />
                    <Button
                        onClick={handleImportTeachers}
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        sx={{ marginTop: 1.5 }}
                    >
                        Aggiungi Docenti
                    </Button>
                </Box>
            </Card>

            {/* SEZIONE STUDENTI */}
            <Card sx={{ padding: 3, marginBottom: 3, boxShadow: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <PersonAddIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Gestione Studenti
                    </Typography>
                </Box>
                <Divider sx={{ marginBottom: 2 }} />

                {/* Tabella studenti */}
                {tabStudents.length > 0 && (
                    <Box sx={{ marginBottom: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1.5 }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                    {labelStudentBooked} ({activeStudents.length})
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Seleziona gli studenti che vuoi rimuovere
                                </Typography>
                            </Box>
                            {selectedStudents.length > 0 && (
                                <Chip
                                    label={`${selectedStudents.length} selezionato/i`}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Paper variant="outlined">
                            <MaterialReactTable table={studentsTable} />
                        </Paper>
                        {selectedStudents.length > 0 && (
                            <Button
                                onClick={() => setShowDeleteConfirm(true)}
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                sx={{ marginTop: 1.5 }}
                            >
                                Rimuovi {selectedStudents.length} studente/i
                            </Button>
                        )}
                    </Box>
                )}

                {/* Form importa studenti */}
                <Box sx={{ marginTop: 2 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 500 }}>
                        Aggiungi nuovi studenti
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: 1 }}>
                        Inserisci gli indirizzi email separati da virgole
                    </Typography>
                    <TextField
                        value={emails}
                        onChange={(e) => setEmails(e.target.value)}
                        placeholder="studente1@campus.unimib.it, studente2@campus.unimib.it"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        size="small"
                    />
                    <Button
                        onClick={handleImportStudents}
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        sx={{ marginTop: 1.5 }}
                    >
                        Aggiungi Studenti
                    </Button>
                </Box>
            </Card>

            {/* MESSAGGI DI FEEDBACK */}
            {importError && (
                <Alert severity="error" onClose={() => setImportError('')} sx={{ marginBottom: 2 }}>
                    <AlertTitle>Errore nell'importazione</AlertTitle>
                    {importError}
                </Alert>
            )}

            {importSuccess && (
                <Alert severity="success" onClose={() => setImportSuccess('')} sx={{ marginBottom: 2 }}>
                    {importSuccess}
                </Alert>
            )}

            {/* DIALOG CONFERMA ELIMINAZIONE STUDENTI */}
            <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
                <DialogTitle>Conferma eliminazione studenti</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sei sicuro di voler rimuovere {selectedStudents.length} studente/i?
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ marginTop: 2, flexWrap: 'wrap' }}>
                        {selectedStudents.map(email => (
                            <Chip key={email} label={email} variant="outlined" size="small" />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
                        Annulla
                    </Button>
                    <Button onClick={handleDeleteSelected} color="error" variant="contained">
                        Rimuovi
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG CONFERMA ELIMINAZIONE DOCENTI */}
            <Dialog open={showDeleteTeacherConfirm} onClose={() => setShowDeleteTeacherConfirm(false)}>
                <DialogTitle>Conferma eliminazione docenti</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sei sicuro di voler rimuovere {selectedTeachers.length} docente/i?
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ marginTop: 2, flexWrap: 'wrap' }}>
                        {selectedTeachers.map(email => (
                            <Chip key={email} label={email} variant="outlined" size="small" />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteTeacherConfirm(false)} color="primary">
                        Annulla
                    </Button>
                    <Button onClick={handleDeleteSelectedTeachers} color="error" variant="contained">
                        Rimuovi
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default StudentList;